
/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { reloadCurrentUser } from '../../../actions/user';
import { useAppSelector } from '../../../reducers';
import { getCurrentUser } from '../../../selectors/user';
import G17Client from '../../../services/G17Client';
import { SiteAlertColors, addSiteAlert } from '../../../slice/siteAlertsSlice';
import config from '../../../config';
import { CompanyAgreement, UserAgreement } from '../../../reducers/current-user';
import { getRootOrg } from '../../../selectors/initiative';
import { RootInitiativeData } from '../../../types/initiative';
import { getDiff } from '../../../utils/date';
import { reloadInitiativeTree } from '../../../actions';
import { AgreementConfig } from '../../../types/app';
import { useAppSettings } from '../../../hooks/app/useAppSettings';

function getRequiredAgreement<T extends string = UserAgreement>(
  configAgreements: AgreementConfig<T>[],
  created: string,
  agreements?: string[],
) {
  return configAgreements.find(config => {
    if (config.fromDate) {
      const diff = getDiff(created, config.fromDate);
      if (diff < 0) {
        return false;
      }
    }

    return !agreements?.includes(config.code);
  });
}

export const AgreementChecker = () => {

  const appSettings = useAppSettings();
  const currentUser = useAppSelector(getCurrentUser);
  const rootOrg = useAppSelector(getRootOrg);

  const requiredUserAgreements = appSettings.userAgreementsRequired ?? [];
  const requiredCompanyAgreements = appSettings.companyAgreementsRequired ?? [];

  if (!currentUser || !rootOrg) {
    return null;
  }

  const userAgreements = Object.keys(currentUser.registrationData?.agreements ?? {});
  const requiredUserAgreement = getRequiredAgreement<UserAgreement>(requiredUserAgreements, currentUser.created, userAgreements);
  if (requiredUserAgreement) {
    switch (requiredUserAgreement.code) {
      case UserAgreement.ESGenomeTandC:
        return <ESGenomeTandC requiredAgreement={requiredUserAgreement} userId={currentUser._id} />;
    }
  }

  const companyAgreements = Object.keys(rootOrg.metadata?.agreements ?? {});
  const requiredCompanyAgreement = getRequiredAgreement<CompanyAgreement>(requiredCompanyAgreements, rootOrg.created, companyAgreements);

  if (requiredCompanyAgreement) {
    switch (requiredCompanyAgreement.code) {
      case CompanyAgreement.CompanyTrackerServicesAgreement:
        return <CompanyTrackerServicesAgreement requiredAgreement={requiredCompanyAgreement} initiative={rootOrg} />;
    }
  }

  return null;
}

const CompanyTrackerServicesAgreement = (props:
  {
    initiative: Pick<RootInitiativeData, '_id' | 'created'>,
    requiredAgreement: AgreementConfig<CompanyAgreement>
  }) => {
  const { initiative, requiredAgreement } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const servicesAgreementLink = <a
    href={'/legal-terms-and-conditions'}
    target='_blank' rel='noreferrer'>
    Platform Services Agreement
  </a>;

  const handleDecline = () => {
    history.push('/');
  }

  const handleAgree = () => {
    G17Client.acceptCompanyAgreement(initiative._id, requiredAgreement.code)
      .then(() => dispatch(reloadInitiativeTree()))
      .catch(() => dispatch(
        addSiteAlert({
          content: 'Could not update agreement. Please try again',
          color: SiteAlertColors.Danger,
          timeout: 5000
        }))
      );
  }

  return (
    <Modal isOpen={true} toggle={handleDecline} backdrop='static'>
      <ModalHeader>Platform Services Agreement</ModalHeader>
      <ModalBody>
        I have read and understood the {servicesAgreementLink},
        which details the terms of my organisation's use of Company Tracker.
        I agree to comply with and be bound by the terms.
      </ModalBody>
      <ModalFooter>
        <Button color='danger' outline onClick={handleDecline}>Decline</Button>
        <Button color='primary' onClick={handleAgree}>Agree</Button>
      </ModalFooter>
    </Modal>
  )
}

const ESGenomeTandC = (props: { userId: string, requiredAgreement: AgreementConfig<UserAgreement> }) => {
  const { userId, requiredAgreement } = props;

  const history = useHistory();
  const dispatch = useDispatch();

  const sgxTandCLink = <a
    href={config.sgx.termsAndConditionsPdf}
    target='_blank' rel='noreferrer'>
    SGXNet User Terms & Conditions
  </a>;

  const esgAddendumLink = <a
    href={config.sgx.addendumTermsLink}
    target='_blank' rel='noreferrer'>
    ESG Portal Addendum to the SGXNet T&Cs
  </a>;

  const handleDecline = () => {
    history.push('/');
  }

  const handleAgree = () => {
    G17Client.acceptUserAgreement(userId, requiredAgreement.code)
      .then(() => dispatch(reloadCurrentUser()))
      .catch(() => dispatch(
        addSiteAlert({
          content: 'Could not update agreement. Please try again',
          timeout: 5000
        }))
      );
  }

  return (
    <Modal isOpen={true} toggle={handleDecline} backdrop='static'>
      <ModalHeader>Welcome to SGX ESGenome's Terms of Use</ModalHeader>
      <ModalBody>
        I have read and understood the {sgxTandCLink} ("SGXNet T&Cs”) and the {esgAddendumLink}.
        I agree to comply with and be bound by the terms in relation to my access and use of this
        website and SGX ESGenome.
      </ModalBody>
      <ModalFooter>
        <Button color='danger' outline onClick={handleDecline}>Decline</Button>
        <Button color='primary' onClick={handleAgree}>Agree</Button>
      </ModalFooter>
    </Modal>
  )
}
